import { timestampDate } from '@bufbuild/protobuf/wkt'
import { ICellRendererParams } from 'ag-grid-community'

import { DocumentSource } from '@/gen/artifacts/v1/model_pb'

import { useIsSupportUser } from '@/lib/app/support-user'
import { useQuickFilterHighlights } from '@/lib/ui/quick-filter-highlights.hook'

import type { ArtifactRow } from '@/pages/company-drawer/artifact/ag-grid-artifact/types'

import { HighlightedText } from '@/components/lemmala-design-system/highlighted-text/highlighted-text'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'
import { ArtifactExpirationDate } from '@/components/shared/artifact-expiration-date/artifact-expiration-date'
import { SubcategorySelect } from '@/components/shared/subcategory-select/subcategory-select'

const env = import.meta.env.MODE
const shouldDisplayCompanySignalScope =
  env === 'development' || env === 'preview' || env === 'staging' || env === 'production'

type ArtifactCellProps = ICellRendererParams<ArtifactRow> & {
  readOnly?: boolean
}

export const ArtifactCell = ({ data, readOnly = false }: ArtifactCellProps) => {
  const isSupportUser = useIsSupportUser()
  const { getHighlights } = useQuickFilterHighlights()

  if (!data?.artifactSubcategory) return <span className='text-main-foreground-tertiary'>–</span>

  const {
    artifactSubcategory,
    artifactCategory,
    expirationDate,
    id,
    artifact,
    source,
    isGroupChild,
  } = data

  const showSubcategorySelect =
    isSupportUser &&
    ((source !== DocumentSource.WEB && artifact.case === 'artifactId') ||
      (source === DocumentSource.WEB && shouldDisplayCompanySignalScope))

  // Get highlights for artifact name
  const nameHighlights = getHighlights(artifactSubcategory)

  return (
    <TextWithIcon
      className='w-full justify-end text-body-default'
      text={
        showSubcategorySelect ? (
          <SubcategorySelect
            category={artifactCategory}
            subcategory={artifactSubcategory}
            documentId={id}
            isPublic={source === DocumentSource.WEB}
          />
        ) : (
          <HighlightedText text={artifactSubcategory} highlights={nameHighlights} />
        )
      }
      icon={
        !readOnly &&
        expirationDate && (
          <ArtifactExpirationDate
            variant={isGroupChild ? 'neutral' : undefined}
            expirationDate={timestampDate(expirationDate)}
          />
        )
      }
      iconPosition='end'
    />
  )
}
